import { isNil } from './nil.no-deps'
import { compose } from './compose.no-deps'
import { not } from './boolean.no-deps'
import { pipe } from './pipe.no-deps'

export function* empty<A>(): Generator<A> {
    // yields nothing
}

export function* singleton<A>(a: A): Generator<A> {
    yield a
}

export function toArray<A>(gen: Generator<A>): A[] {
    return [...gen]
}

export function* fromArray<A>(array: A[]): Generator<A> {
    for (const m of array) yield m
}

export function map<A, B>(fn: (member: A, index: number) => B) {
    return andThen<A, B>((m, i) => singleton(fn(m, i)))
}

export function reduce<A, B>(initial: B, fn: (accumulator: B, member: A, index: number) => B) {
    return (gen: Generator<A>): B => toArray(gen).reduce(fn, initial)
}

export function andThen<A, B>(fn: (member: A, index: number) => Generator<B>) {
    return function* (gen: Generator<A>): Generator<B> {
        let i = 0
        for (const m of gen) {
            yield* fn(m, i)
            i++
        }
    }
}

export function filter<T, A extends T>(fn: (m: T) => m is A): (gen: Generator<T>) => Generator<A>
export function filter<T>(fn: (m: T) => boolean): (gen: Generator<T>) => Generator<T>
export function filter<T>(fn: (m: T) => boolean) {
    return function* (gen: Generator<T>): Generator<T> {
        for (const m of gen) {
            if (fn(m)) yield m
        }
    }
}

export function filterOut<T>(fn: (m: T) => boolean) {
    const notFn = compose(fn, not)
    return filter(notFn)
}

export function find<T>(fn: (value: T, index: number) => boolean) {
    return (gen: Generator<T>): T => {
        let i = 0
        for (const m of gen) {
            if (fn(m, i)) return m
            i++
        }
        return null
    }
}

export function some<T>(fn: (value: T, index: number) => boolean) {
    return (gen: Generator<T>): boolean => {
        let i = 0
        for (const m of gen) {
            if (fn(m, i)) return true
            i++
        }
        return false
    }
}

export function every<T>(fn: (value: T, index: number) => boolean) {
    return (gen: Generator<T>): boolean => {
        let i = 0
        for (const m of gen) {
            if (!fn(m, i)) return false
            i++
        }
        return true
    }
}

export function slice(start: number, end?: number) {
    return function* <T>(gen: Generator<T>): Generator<T> {
        let i = 0
        for (const m of gen) {
            if (i >= start && i < end) {
                yield m
            }
            i++
        }
    }
}

export function* concat<T>(a: Generator<T>, b: Generator<T>): Generator<T> {
    yield* a
    yield* b
}

export function flatten<T>(gen: Generator<Generator<T>>): Generator<T> {
    return pipe(gen, reduce(empty(), concat))
}

export function* uniq<T>(gen: Generator<T>): Generator<T> {
    const set = new Set<T>()

    for (const m of gen) {
        if (set.has(m)) continue
        yield m
        set.add(m)
    }
}

export const compact = filterOut(isNil)

export function head<T>(gen: Generator<T>) {
    for (const m of gen) {
        return m
    }
}

export function last<T>(gen: Generator<T>) {
    let value: T
    for (const m of gen) {
        value = m
    }
    return value
}

export function dropWhile<T>(fn: (value: T, index: number) => boolean) {
    return function* (gen: Generator<T>): Generator<T> {
        let i = 0
        let found = false
        for (const m of gen) {
            if (!fn(m, i)) found = true
            if (found) yield m
            i++
        }
    }
}

export function dropN(n: number): <T>(gen: Generator<T>) => Generator<T> {
    return dropWhile((_, i) => i < n)
}
